/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/pages/partners/SupplierForm.tsx
 * ----------------------------------------------------------------------------
 * Formulário simples (criar/editar) de Fornecedor.
 *
 * Notas de manutenção:
 * - O backend expõe /api/partners/suppliers/ (POST para criar; GET/PUT para editar).
 * - Campos tolerantes de schema: 'edi_code' é o nome preferido (legado pode usar 'edi').
 * - Contatos (SupplierContact) podem ser feitos numa tela própria depois; aqui focamos
 *   nos dados do fornecedor para o cadastro manual funcionar já.
 * ========================================================================== */

import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '@/lib/api'

type SupplierDTO = {
  id?: number
  code: string
  name: string
  edi_code?: string | null
  edi?: string | null
  email?: string | null
  phone?: string | null
  priority?: number
  is_active?: boolean
  note?: string | null
  country?: string | null
  state?: string | null
  city?: string | null
  postcode?: string | null
  street1?: string | null
  street2?: string | null
}

export default function SupplierForm() {
  const { id } = useParams<{ id: string }>()
  const editing = !!id
  const navigate = useNavigate()

  // estado do form
  const [form, setForm] = useState<SupplierDTO>({
    code: '',
    name: '',
    edi_code: '',
    email: '',
    phone: '',
    priority: 3,
    is_active: true,
    note: '',
    country: '',
    state: '',
    city: '',
    postcode: '',
    street1: '',
    street2: '',
  })

  // carrega dados do fornecedor quando em edição
  const { data, isFetching } = useQuery({
    queryKey: ['supplier', id],
    enabled: editing,
    queryFn: async () =>
      (await api.get(`/api/partners/suppliers/${id}/`)).data as SupplierDTO,
  })

  useEffect(() => {
    if (data) {
      setForm({
        code: data.code ?? '',
        name: data.name ?? '',
        // tolerante a schema: se vier 'edi', usa; senão 'edi_code'
        edi_code: (data.edi_code ?? data.edi ?? '') || '',
        email: data.email ?? '',
        phone: data.phone ?? '',
        priority: data.priority ?? 3,
        is_active: data.is_active ?? true,
        note: data.note ?? '',
        country: data.country ?? '',
        state: data.state ?? '',
        city: data.city ?? '',
        postcode: data.postcode ?? '',
        street1: data.street1 ?? '',
        street2: data.street2 ?? '',
      })
    }
  }, [data])

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        edi_code: (form.edi_code ?? '').trim() || null,
        email: (form.email ?? '').trim() || null,
        phone: (form.phone ?? '').trim() || null,
        priority: Number(form.priority || 3),
        is_active: !!form.is_active,
        note: (form.note ?? '').trim() || null,
        country: (form.country ?? '').trim() || null,
        state: (form.state ?? '').trim() || null,
        city: (form.city ?? '').trim() || null,
        postcode: (form.postcode ?? '').trim() || null,
        street1: (form.street1 ?? '').trim() || null,
        street2: (form.street2 ?? '').trim() || null,
      }

      if (editing) {
        const { data } = await api.put(
          `/api/partners/suppliers/${id}/`,
          payload,
        )
        return data
      } else {
        const { data } = await api.post(`/api/partners/suppliers/`, payload)
        return data
      }
    },
    onSuccess: () => navigate('/partners/suppliers'),
  })

  const onChange = (k: keyof SupplierDTO, v: any) =>
    setForm((s) => ({ ...s, [k]: v }))

  return (
    <div className="grid gap-4">
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {editing ? 'Editar fornecedor' : 'Novo fornecedor'}
          </h2>
          <div className="flex gap-2">
            <button
              className="btn subtle"
              onClick={() => navigate('/partners/suppliers')}
            >
              Cancelar
            </button>
            <button
              className="btn"
              onClick={() => save.mutate()}
              disabled={
                save.isPending || !form.code.trim() || !form.name.trim()
              }
            >
              {save.isPending ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </div>

        {/* Básico */}
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="grid gap-1">
            <span className="text-sm">Código *</span>
            <input
              className="input"
              value={form.code}
              onChange={(e) => onChange('code', e.target.value)}
              disabled={isFetching || save.isPending}
            />
          </label>
          <label className="grid gap-1 md:col-span-2">
            <span className="text-sm">Nome *</span>
            <input
              className="input"
              value={form.name}
              onChange={(e) => onChange('name', e.target.value)}
              disabled={isFetching || save.isPending}
            />
          </label>

          <label className="grid gap-1">
            <span className="text-sm">EDI</span>
            <input
              className="input"
              value={form.edi_code ?? ''}
              onChange={(e) => onChange('edi_code', e.target.value)}
            />
          </label>
          <label className="grid gap-1">
            <span className="text-sm">Email</span>
            <input
              className="input"
              value={form.email ?? ''}
              onChange={(e) => onChange('email', e.target.value)}
            />
          </label>
          <label className="grid gap-1">
            <span className="text-sm">Telefone</span>
            <input
              className="input"
              value={form.phone ?? ''}
              onChange={(e) => onChange('phone', e.target.value)}
            />
          </label>
          <label className="grid gap-1">
            <span className="text-sm">Prioridade (1 alta → 5 baixa)</span>
            <input
              className="input"
              type="number"
              min={1}
              max={5}
              value={form.priority ?? 3}
              onChange={(e) => onChange('priority', Number(e.target.value))}
            />
          </label>

          <label className="grid gap-1">
            <span className="text-sm">Ativo</span>
            <select
              className="input"
              value={form.is_active ? '1' : '0'}
              onChange={(e) => onChange('is_active', e.target.value === '1')}
            >
              <option value="1">Sim</option>
              <option value="0">Não</option>
            </select>
          </label>
        </div>

        {/* Endereço */}
        <div className="mt-5">
          <h3 className="font-semibold text-sm opacity-80">Endereço</h3>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-3">
            <label className="grid gap-1 md:col-span-2">
              <span className="text-sm">Rua</span>
              <input
                className="input"
                value={form.street1 ?? ''}
                onChange={(e) => onChange('street1', e.target.value)}
              />
            </label>
            <label className="grid gap-1 md:col-span-2">
              <span className="text-sm">Complemento</span>
              <input
                className="input"
                value={form.street2 ?? ''}
                onChange={(e) => onChange('street2', e.target.value)}
              />
            </label>
            <label className="grid gap-1">
              <span className="text-sm">Cidade</span>
              <input
                className="input"
                value={form.city ?? ''}
                onChange={(e) => onChange('city', e.target.value)}
              />
            </label>
            <label className="grid gap-1">
              <span className="text-sm">Estado/Região</span>
              <input
                className="input"
                value={form.state ?? ''}
                onChange={(e) => onChange('state', e.target.value)}
              />
            </label>
            <label className="grid gap-1">
              <span className="text-sm">CEP/Código postal</span>
              <input
                className="input"
                value={form.postcode ?? ''}
                onChange={(e) => onChange('postcode', e.target.value)}
              />
            </label>
            <label className="grid gap-1">
              <span className="text-sm">País (ISO-2, ex.: BR, PT)</span>
              <input
                className="input"
                value={form.country ?? ''}
                onChange={(e) =>
                  onChange('country', e.target.value.toUpperCase())
                }
                maxLength={2}
              />
            </label>
          </div>
        </div>

        {/* Observações */}
        <div className="mt-5">
          <h3 className="font-semibold text-sm opacity-80">Observações</h3>
          <textarea
            className="input mt-2 min-h-[100px]"
            value={form.note ?? ''}
            onChange={(e) => onChange('note', e.target.value)}
          />
        </div>

        {save.isError && (
          <div className="mt-3 text-sm text-red-600">
            Erro ao salvar: {String((save.error as any)?.message || '—')}
          </div>
        )}
      </div>
    </div>
  )
}
